import { ActionIcon, Menu, Text } from "@marketplace/ui";
import { notifications } from "@marketplace/ui/notifications";
import { useSetAtom } from "jotai";
import { useTranslation } from "react-i18next";
import { MdMoreVert } from "react-icons/md";

import { RouterLink } from "@/components";
import { usePermissions } from "@/hooks";

import {
  changePublishStatusModalAtom,
  deleteProductModalAtom,
  platformFeeInputModalAtom,
  setPromoModalAtom
} from "../../atoms/product-list-page-atom";
import type { ProductListItem } from "../../types";

type ProductListTableActionsProps = {
  productData: ProductListItem;
};
export const ProductListTableActions = ({
  productData
}: ProductListTableActionsProps) => {
  const { t } = useTranslation("product", {
    keyPrefix: "productListPage.tableActions"
  });
  const setDeleteProductModal = useSetAtom(deleteProductModalAtom);
  const setChangePublishStatusModal = useSetAtom(changePublishStatusModalAtom);
  const setPlatformFeeInputModal = useSetAtom(platformFeeInputModalAtom);
  const setSetPromoModal = useSetAtom(setPromoModalAtom);
  const { isSuperAdmin } = usePermissions();

  const handleDelete = () => {
    try {
      setDeleteProductModal({
        visible: true,
        data: {
          ...productData,
          ...(productData.key && { id: productData.key })
        }
      });
    } catch {
      notifications.show({
        variant: "error",
        message: t("deleteError")
      });
    }
  };

  const handleChangePublishStatus = () => {
    setChangePublishStatusModal({
      opened: true,
      productId: productData.id,
      currentStatus: productData.product_status,
      isPublish: productData.is_publish
    });
  };

  const handleEditMargin = () => {
    setPlatformFeeInputModal({
      opened: true,
      productSlug: productData.slug
    });
  };

  const isDraft = !!productData.key;
  const isPublish =
    productData.product_status === "active" || productData.is_publish === true;
  const isInReview = productData.product_status === "in_review";

  const getProductSlug = () =>
    isPublish || isInReview ? productData.slug : (productData.key ?? "");

  const getSearchParams = () => ({
    draft: isDraft ? true : undefined
  });

  return (
    <Menu width={120} position="bottom-end">
      <Menu.Target>
        <ActionIcon variant="transparent" className="text-fjord-grey-500">
          <MdMoreVert size={24} />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Item
          classNames={{
            item: "!p-0"
          }}
        >
          <RouterLink
            className="p-[14px] block"
            to="/products/list/$slug/edit"
            underline="never"
            params={{
              slug: getProductSlug()
            }}
            search={getSearchParams()}
          >
            <Text size="s" className="text-fjord-grey-500">
              {t("editProduct")}
            </Text>
          </RouterLink>
        </Menu.Item>
        {!isInReview && !isDraft && (
          <>
            <Menu.Item onClick={handleChangePublishStatus}>
              <Text size="s" className="text-fjord-grey-500">
                {productData.is_publish === true
                  ? t("unpublish")
                  : t("publish")}
              </Text>
            </Menu.Item>

            <Menu.Item>
              <RouterLink
                to="/products/list/$slug/review"
                underline="never"
                params={{
                  slug: getProductSlug()
                }}
                search={getSearchParams()}
                target="_blank"
              >
                <Text size="s" className="text-fjord-grey-500">
                  {t("preview")}
                </Text>
              </RouterLink>
            </Menu.Item>
          </>
        )}

        {!isInReview && !isDraft && isSuperAdmin && (
          <>
            <Menu.Item onClick={handleEditMargin}>
              <Text size="s" className="text-fjord-grey-500">
                {t("editMargin")}
              </Text>
            </Menu.Item>
            <Menu.Item
              onClick={() => {
                setSetPromoModal({
                  opened: true,
                  productId: productData.id,
                  isPromoAvailable: productData.is_promo_available
                });
              }}
            >
              <Text size="s" className="text-fjord-grey-500">
                {productData.is_promo_available
                  ? t("deactivatePromo")
                  : t("setPromo")}
              </Text>
            </Menu.Item>
          </>
        )}

        <Menu.Item onClick={handleDelete}>
          <Text size="s" className="text-fjord-grey-500">
            {t("delete")}
          </Text>
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};
